import { useEffect, useState } from "react";
import { Mic, Loader2, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/Layout/AppShell";
import { InterviewsNewButton } from "../components/Recording/StartInterviewButton";
import { useAuth } from "../lib/auth-context";
import api from "../lib/api";

const STROKE = 1.5;

const STATUS_LABEL = {
  scheduled: "Scheduled",
  recording: "Recording",
  transcribed: "Transcribed",
  reviewed: "Reviewed",
};
const STATUS_STYLE = {
  scheduled: "bg-ink-100 text-ink-700 border-ink-200",
  recording: "bg-amber-50 text-amber-800 border-amber-200",
  transcribed: "bg-emerald-50 text-emerald-800 border-emerald-800/15",
  reviewed: "bg-emerald-50 text-emerald-800 border-emerald-800/15",
};

export default function Interviews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { member } = useAuth();
  const navigate = useNavigate();
  const isHm = member?.role === "hiring_manager";

  useEffect(() => {
    api
      .get("/interviews")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppShell>
      <div data-testid="interviews-page" className="space-y-8">
        <header className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="hp-section-label">Interviews</div>
            <h1 className="font-display mt-2 text-3xl font-medium tracking-tight text-ink-900">
              {isHm ? "Interviews assigned to you" : "All interviews"}
            </h1>
            <p className="mt-1 text-[14px] text-ink-500">
              {isHm
                ? "Only interviews you have been added to as an interviewer appear here."
                : "Recorded interviews, transcripts, and summaries — the official record."}
            </p>
          </div>
          <InterviewsNewButton />
        </header>

        {loading && (
          <div className="hp-card flex justify-center p-12">
            <Loader2 className="h-5 w-5 animate-spin text-ink-400" strokeWidth={STROKE} />
          </div>
        )}

        {!loading && items.length === 0 && (
          isHm ? (
            <div
              className="hp-card flex flex-col items-center justify-center px-6 py-16 text-center"
              data-testid="hm-empty-state"
            >
              <Mic className="h-6 w-6 text-ink-300" strokeWidth={STROKE} />
              <h2 className="font-display mt-4 text-xl font-medium text-ink-900">
                No interviews assigned to you yet
              </h2>
              <p className="mt-2 max-w-md text-[14px] text-ink-500">
                A recruiter or admin needs to add you as an interviewer on a specific interview before it appears here.
              </p>
            </div>
          ) : (
            <div className="hp-card flex flex-col items-center justify-center px-6 py-16 text-center" data-testid="interviews-empty">
              <Mic className="h-6 w-6 text-ink-300" strokeWidth={STROKE} />
              <h2 className="font-display mt-4 text-xl font-medium text-ink-900">No interviews yet</h2>
              <p className="mt-2 max-w-md text-[14px] text-ink-500">
                Record your first interview to start building the official record.
              </p>
              <div className="mt-6">
                <InterviewsNewButton />
              </div>
            </div>
          )
        )}

        {!loading && items.length > 0 && (
          <div className="hp-card overflow-hidden">
            <table className="w-full" data-testid="interviews-table">
              <thead className="bg-ink-50 border-b border-ink-200">
                <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-ink-500">
                  <th className="px-5 py-3">Title</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Round</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Created</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {items.map((i) => (
                  <tr
                    key={i.interview_id}
                    onClick={() => navigate(`/interviews/${i.interview_id}`)}
                    className="hover:bg-ink-50 transition cursor-pointer"
                    data-testid={`interview-row-${i.interview_id}`}
                  >
                    <td className="px-5 py-3 text-[14px] font-medium text-ink-900">
                      <div className="flex items-center gap-2">
                        {i.consent_obtained && (
                          <ShieldCheck className="h-3.5 w-3.5 text-emerald-800" strokeWidth={STROKE} />
                        )}
                        {i.title || "Untitled interview"}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-[13px] text-ink-700 capitalize">{i.type}</td>
                    <td className="px-5 py-3 text-[13px] text-ink-700">{i.round_number}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[i.status] || "bg-ink-100 text-ink-700 border-ink-200"}`}>
                        {STATUS_LABEL[i.status] || i.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[12px] text-ink-500">
                      {new Date(i.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3 text-right text-[13px] text-emerald-800">Open →</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
